package br.com.texeratx.boardgametimeapp.data.repository

import br.com.texeratx.boardgametimeapp.core.network.ApiClient
import br.com.texeratx.boardgametimeapp.core.session.SessionManager
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanInviteDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanJoinRequestDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.ClanMemberDTO
import br.com.texeratx.boardgametimeapp.data.model.clan.CreateClanRequest
import br.com.texeratx.boardgametimeapp.data.model.clan.CreateInviteRequest
import br.com.texeratx.boardgametimeapp.data.model.clan.JoinClanRequestBody
import br.com.texeratx.boardgametimeapp.data.model.clan.JoinClanResponse
import br.com.texeratx.boardgametimeapp.data.model.clan.UpdateClanRequest
import br.com.texeratx.boardgametimeapp.data.model.clan.UpdateMemberRoleRequest
import br.com.texeratx.boardgametimeapp.data.model.clan.UserClanMembershipDTO
import br.com.texeratx.boardgametimeapp.data.model.login.ChangePasswordRequest
import br.com.texeratx.boardgametimeapp.data.model.login.UpdateUserRequest
import br.com.texeratx.boardgametimeapp.data.model.login.UserDTO
import io.ktor.client.call.body
import io.ktor.client.statement.bodyAsText
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

class ClanRepository(
    private val api: ApiClient,
    private val sessionManager: SessionManager
) {

    suspend fun getMyUser(): Result<UserDTO> = runCatching {
        api.get("/me").body<UserDTO>()
    }

    suspend fun updateMyUser(displayName: String?, imageUrl: String?): Result<UserDTO> = runCatching {
        api.put("/me", UpdateUserRequest(displayName = displayName, imageUrl = imageUrl)).body<UserDTO>()
    }

    suspend fun changePassword(currentPassword: String, newPassword: String): Result<String> = runCatching {
        val req = ChangePasswordRequest(currentPassword, newPassword)
        val response = api.put("/me/password", req)
        val rawBody = response.bodyAsText()
        if (response.status.value !in 200..299) {
            throw IllegalStateException(parseError(rawBody) ?: "Erro ao alterar senha")
        }
        parseMessage(rawBody).ifBlank { "Senha alterada com sucesso" }
    }

    suspend fun getMyClans(): Result<List<UserClanMembershipDTO>> = runCatching {
        val r = api.get("/me/clans")
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao carregar clãs")
        r.body<List<UserClanMembershipDTO>>()
    }

    suspend fun createClan(name: String, imageUrl: String?): Result<UserClanMembershipDTO> = runCatching {
        val r = api.post("/clans", CreateClanRequest(name = name.trim(), imageUrl = imageUrl))
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao criar clã")
        r.body<UserClanMembershipDTO>()
    }

    suspend fun joinClan(code: String): Result<JoinClanResponse> = runCatching {
        val r = api.post("/clans/join", JoinClanRequestBody(code.trim().uppercase()))
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Código inválido")
        r.body<JoinClanResponse>()
    }

    suspend fun getActiveClan(): Result<ClanDTO> = runCatching {
        api.get("/clan/me").body<ClanDTO>()
    }

    suspend fun updateActiveClan(name: String, imageUrl: String?, requireApproval: Boolean?): Result<ClanDTO> = runCatching {
        api.put(
            "/clan/me",
            UpdateClanRequest(name = name.trim(), imageUrl = imageUrl, requireApproval = requireApproval)
        ).body<ClanDTO>()
    }

    suspend fun leaveClan(userId: Long, clanId: Long): Result<Unit> = runCatching {
        val r = api.delete("/clans/$clanId/members/$userId")
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao sair do clã")
        Unit
    }

    suspend fun listMembers(clanId: Long): Result<List<ClanMemberDTO>> = runCatching {
        api.get("/clans/$clanId/members").body<List<ClanMemberDTO>>()
    }

    suspend fun updateMemberRole(clanId: Long, targetUserId: Long, role: String): Result<Unit> = runCatching {
        val r = api.patch("/clans/$clanId/members/$targetUserId", UpdateMemberRoleRequest(role = role))
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao atualizar role")
        Unit
    }

    suspend fun removeMember(clanId: Long, targetUserId: Long): Result<Unit> = runCatching {
        val r = api.delete("/clans/$clanId/members/$targetUserId")
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao remover membro")
        Unit
    }

    suspend fun listInvites(clanId: Long): Result<List<ClanInviteDTO>> = runCatching {
        api.get("/clans/$clanId/invites").body<List<ClanInviteDTO>>()
    }

    suspend fun createInvite(clanId: Long, expiresAt: String?, maxUses: Int?): Result<ClanInviteDTO> = runCatching {
        api.post("/clans/$clanId/invites", CreateInviteRequest(expiresAt, maxUses)).body<ClanInviteDTO>()
    }

    suspend fun revokeInvite(clanId: Long, inviteId: Long): Result<Unit> = runCatching {
        val r = api.delete("/clans/$clanId/invites/$inviteId")
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao revogar convite")
        Unit
    }

    suspend fun listPendingJoinRequests(clanId: Long): Result<List<ClanJoinRequestDTO>> = runCatching {
        api.get("/clans/$clanId/join-requests").body<List<ClanJoinRequestDTO>>()
    }

    suspend fun approveJoinRequest(clanId: Long, requestId: Long): Result<Unit> = runCatching {
        val r = api.post("/clans/$clanId/join-requests/$requestId/approve", emptyMap<String, String>())
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao aprovar")
        Unit
    }

    suspend fun rejectJoinRequest(clanId: Long, requestId: Long): Result<Unit> = runCatching {
        val r = api.post("/clans/$clanId/join-requests/$requestId/reject", emptyMap<String, String>())
        if (r.status.value !in 200..299) throw IllegalStateException(parseError(r.bodyAsText()) ?: "Erro ao rejeitar")
        Unit
    }

    private fun parseMessage(rawBody: String): String {
        if (rawBody.isBlank()) return ""
        return runCatching {
            Json.parseToJsonElement(rawBody).jsonObject["message"]?.jsonPrimitive?.content.orEmpty()
        }.getOrDefault("")
    }

    private fun parseError(rawBody: String): String? {
        if (rawBody.isBlank()) return null
        return runCatching {
            val obj = Json.parseToJsonElement(rawBody).jsonObject
            obj["error"]?.jsonPrimitive?.content ?: obj["message"]?.jsonPrimitive?.content
        }.getOrNull()
    }
}
